import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";

import {
  Loader,
  MenuOptionSpec,
  NotificationType,
  Toolbar,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import { WarningMessages } from "@transfr-inc/dashboard-components";
import {
  DeleteCategorySuccess,
  DeleteMenuSuccess,
  GenericError,
} from "@components/utils/notification.constants";
import { CATEGORY, MENU, PROGRAM } from "@constants/template-types";
import { MenuBuilderContext, SessionContext } from "@src/context";
import { services } from "@src/dependencies";
import {
  useIndependentTabLoading,
  useNotification,
} from "@src/hooks";
import { useEditWarningModal } from "@transfr-inc/dashboard-components/hooks";

import { DeleteMenuModal } from "../menus/detail/components/menu-builder/modals";
import { DeleteCategoryModal } from "../menus/detail/components/menu-builder/modals/delete-category/delete-category.modal";
import { EditCategoryModal } from "../menus/detail/components/menu-builder/modals/edit-category/edit-category.modal";
import { EditMenuDetailsModal } from "../menus/detail/components/modals/edit-menu-details/edit-menu-details.modal";
import NewTemplateModal from "../new-template/new-template-modal";
import { DeletionModal } from "@transfr-inc/dashboard-components/curriculum";
import { TemplatesTabs } from "./components/templates.tabs";

import "./index.scss";

export const TemplateList = ({ responsiveStyles }) => {
  const { menuBuilderService, programsService } = services;

  const history = useHistory();
  const { getAllCategories, getAllModules } = useContext(MenuBuilderContext);
  const { activeTab, setActiveTab } = useContext(SessionContext);
  const { addNotification } = useNotification();
  const {
    tabStates,
    setTabLoading,
    setTabError,
    setTabData,
    retryTab,
    getTabState,
  } = useIndependentTabLoading({
    menus: { loading: false, error: undefined, data: [] },
    categories: { loading: false, error: undefined, data: [] },
    programs: { loading: false, error: undefined, data: [] },
  });

  const [loading, setLoading] = useState();
  const [deleteMenuModalOpen, setDeleteMenuModalOpen] = useState();
  const [editMenuModalOpen, setEditMenuModalOpen] = useState();
  const [deleteCategoryModalOpen, setDeleteCategoryModalOpen] = useState();
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState();
  const [selectedMenu, setSelectedMenu] = useState();
  const [selectedCategory, setSelectedCategory] = useState();
  const [deleteProgramModalOpen, setDeleteProgramModalOpen] = useState();
  const [selectedProgram, setSelectedProgram] = useState();

  const [deleteMenuWarningModal, openDeleteMenuWarningModal] =
    useEditWarningModal({
      title: WarningMessages.menu.title,
      message: WarningMessages.menu.delete,
      icon: ["fa-solid", "fa-clock-rotate-left"],
      iconColor: "danger-red",
    });
  const [deleteCategoryWarningModal, openDeleteCategoryWarningModal] =
    useEditWarningModal({
      title: WarningMessages.category.title,
      message: WarningMessages.category.delete,
      icon: ["fa-solid", "fa-clock-rotate-left"],
      iconColor: "danger-red",
    });
  const [deleteProgramWarningModal, openDeleteProgramWarningModal] =
    useEditWarningModal({
      title: WarningMessages.program.title,
      message: WarningMessages.program.delete,
      icon: ["fa-solid", "fa-clock-rotate-left"],
      iconColor: "danger-red",
    });
  // Modal
  const [openCreateTemplate, setOpenCreateTemplate] = useState(false);

  const fetchMenuData = async () => {
    try {
      setTabLoading("menus", true);
      const menuData = await menuBuilderService.getAllMenus();
      const mappedMenus = menuData.map((data) => ({
        ...data,
        author: {
          firstName: data.last_edited_by.first_name,
          lastName: data.last_edited_by.last_name,
        },
      }));
      setTabData("menus", mappedMenus);
    } catch (error) {
      console.error("Failed to fetch menus:", error);
      setTabError("menus", error);
    }
  };

  const fetchCategoryData = async () => {
    try {
      setTabLoading("categories", true);
      const categoryData = await menuBuilderService.getTemplateCategories();
      setTabData("categories", categoryData);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setTabError("categories", error);
    }
  };

  const fetchProgramData = async () => {
    try {
      setTabLoading("programs", true);
      const programData = await programsService.getAllPrograms();
      setTabData("programs", programData);
    } catch (error) {
      console.error("Failed to fetch programs:", error);
      setTabError("programs", error);
    }
  };

  useEffect(() => {
    fetchMenuData();
    fetchCategoryData();
    fetchProgramData();
  }, []);

  const handleRetryTab = (tabName) => {
    switch (tabName) {
      case "menus": {
        retryTab("menus", fetchMenuData);
        break;
      }
      case "categories": {
        retryTab("categories", fetchCategoryData);
        break;
      }
      case "programs": {
        retryTab("programs", fetchProgramData);
        break;
      }
      default:
        console.warn(`Unknown tab: ${tabName}`);
    }
  };

  const onOpenDeleteMenuModal = (_, data) => {
    setSelectedMenu(data);
    if (data.organizations.length > 0) {
      openDeleteMenuWarningModal();
    } else {
      setDeleteMenuModalOpen(true);
    }
  };

  const onDeleteMenu = async (data) => {
    try {
      await menuBuilderService.deleteTemplateCategory(data.id);
      showNotification(
        NotificationType.success,
        DeleteMenuSuccess(data),
        "circle-check",
      );
      fetchMenuData();
    } catch (error) {
      console.error("Failed to delete menu:", error);
      showNotification(NotificationType.error, GenericError);
    }
  };

  const onOpenEditMenuModal = (_, data) => {
    setSelectedMenu(data);
    setEditMenuModalOpen(true);
  };

  const onEditMenu = (updatedMenuData) => {
    const currentMenus = getTabState("menus").data || [];
    const updatedMenus = currentMenus.map((menu) => {
      if (menu.id !== updatedMenuData.id) {
        return menu;
      }
      return {
        ...menu,
        ...updatedMenuData,
      };
    });
    setTabData("menus", updatedMenus);
  };

  const onOpenDeleteCategoryModal = (_, data) => {
    setSelectedCategory(data);
    if (data.menus.length > 0) {
      openDeleteCategoryWarningModal();
    } else {
      setDeleteCategoryModalOpen(true);
    }
  };

  const onDeleteCategory = async (data) => {
    setLoading(true);
    try {
      await menuBuilderService.deleteCategory(data.id);
      showNotification(
        NotificationType.success,
        DeleteCategorySuccess(data),
        "circle-check",
      );
      fetchCategoryData();
    } catch {
      showNotification(NotificationType.error, GenericError);
    } finally {
      setLoading();
    }
  };

  const onDeleteProgram = async (data) => {
    setLoading(true);
    try {
      await programsService.deleteCourseTemplate(data.id);
      showNotification(
        NotificationType.success,
        "Program successfully deleted",
        "circle-check",
      );
      fetchProgramData();
    } catch {
      showNotification(NotificationType.error, GenericError);
    } finally {
      setLoading();
    }
  };

  const onOpenEditCategoryModal = (_, data) => {
    setSelectedCategory(data);
    setEditCategoryModalOpen(true);
  };
  const onOpenDeleteProgramModal = (_, data) => {
    setSelectedProgram(data);
    if (data?.organizations?.length > 0) {
      openDeleteProgramWarningModal();
    } else {
      setDeleteProgramModalOpen(true);
    }
  };

  const onEditCategory = (updatedCategoryData) => {
    const currentCategories = getTabState("categories").data || [];
    const updatedCategories = currentCategories.map((category) => {
      if (category.id !== updatedCategoryData.id) {
        return category;
      }
      return {
        ...category,
        ...updatedCategoryData,
      };
    });
    setTabData("categories", updatedCategories);
  };

  const actionMenuOptions = {
    menu: {
      delete: new MenuOptionSpec(
        "Delete",
        ["fa-regular", "fa-trash-can"],
        onOpenDeleteMenuModal,
      ),
      edit: new MenuOptionSpec(
        "Edit Menu Details",
        ["fa-regular", "fa-edit"],
        onOpenEditMenuModal,
      ),
    },
    category: {
      delete: new MenuOptionSpec(
        "Delete",
        ["fa-regular", "fa-trash-can"],
        onOpenDeleteCategoryModal,
      ),
      edit: new MenuOptionSpec(
        "Edit Category Details",
        ["fa-regular", "fa-edit"],
        onOpenEditCategoryModal,
      ),
    },
    program: {
      delete: new MenuOptionSpec(
        "Delete",
        ["fa-regular", "fa-trash-can"],
        onOpenDeleteProgramModal,
      ),
      //un comment when it's implemented
      // edit: new MenuOptionSpec(
      //   "Edit Program Details",
      //   ["fa-regular", "fa-edit"],
      //   onOpenEditCategoryModal
      // ),
    },
  };

  const templateData = useMemo(() => {
    return {
      menuData: getTabState("menus")?.data || [],
      categoryData: getTabState("categories")?.data || [],
      programData: getTabState("programs")?.data || [],
    };
  }, [getTabState]);

  const toolbar = (
    <Toolbar>
      <Button
        icon={["fa-regular", "fa-plus"]}
        primary
        title={"Create Template"}
        onClick={() => setOpenCreateTemplate(true)}
      ></Button>
    </Toolbar>
  );

  const pageHeader = <PageHeader title={"Templates"}>{toolbar}</PageHeader>;

  const onTemplateCreated = async (template, templateID) => {
    getAllCategories();
    getAllModules();
    goDetailPage(template, templateID);
  };

  const goDetailPage = (template, templateID) => {
    setOpenCreateTemplate();

    let tabName;
    switch (templateID) {
      case MENU.id:
        tabName = "templates-menus";
        break;
      case CATEGORY.id:
        tabName = "templates-categories";
        break;
      case PROGRAM.id:
        tabName = "templates-programs";
        break;
      default:
        tabName = "templates-menus";
    }

    switch (templateID) {
      case MENU.id: {
        const { id } = template;
        history.push({
          pathname: `templates/menus/${id}`,
          state: {
            menuId: id,
            data: template,
            breadcrumbLabel: "templates",
            returnTab: tabName,
          },
        });
        break;
      }
      case CATEGORY.id: {
        const { id } = template;
        history.push({
          pathname: `templates/categories/${id}`,
          state: {
            data: template,
            breadcrumbLabel: "templates",
            returnTab: tabName,
          },
        });
        break;
      }
      case PROGRAM.id: {
        const { id } = template;
        history.push({
          pathname: `templates/programs/${id}`,
          state: {
            breadcrumbLabel: "templates",
            returnTab: tabName,
          },
        });
        break;
      }
      default:
        history.push({
          pathname: `templates`,
          state: {
            data: template,
            breadcrumbLabel: "templates",
            returnTab: tabName,
          },
        });
    }
  };

  const showNotification = (type, message, icon) => {
    addNotification({ type, message, icon });
  };

  return (
    <PageLayout
      className="templates-list-page"
      responsiveStyles={responsiveStyles}
      header={pageHeader}
    >
      {loading && <Loader overlay></Loader>}
      <TemplatesTabs
        data={templateData}
        responsiveStyles={responsiveStyles}
        actionMenuOptions={actionMenuOptions}
        tabStates={tabStates}
        onRetryTab={handleRetryTab}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <NewTemplateModal
        show={openCreateTemplate}
        setShow={() => setOpenCreateTemplate()}
        onTemplateCreated={onTemplateCreated}
      />
      {deleteMenuModalOpen && (
        <DeleteMenuModal
          open={deleteMenuModalOpen}
          onCancel={() => setDeleteMenuModalOpen()}
          onDeleteMenu={() => onDeleteMenu(selectedMenu)}
          title={selectedMenu?.name}
          setNotification={() => {}}
        />
      )}
      {editMenuModalOpen && (
        <EditMenuDetailsModal
          menu={selectedMenu}
          open={editMenuModalOpen}
          onEditMenuDetailsHandler={onEditMenu}
          onCancel={() => setEditMenuModalOpen()}
          showNotification={showNotification}
        ></EditMenuDetailsModal>
      )}
      {deleteCategoryModalOpen && (
        <DeleteCategoryModal
          open={deleteCategoryModalOpen}
          onCancel={() => setDeleteCategoryModalOpen()}
          onDeleteCategory={() => onDeleteCategory(selectedCategory)}
          category={selectedCategory}
        ></DeleteCategoryModal>
      )}
      {editCategoryModalOpen && (
        <EditCategoryModal
          category={selectedCategory}
          open={editCategoryModalOpen}
          onCancel={() => setEditCategoryModalOpen()}
          onEditCategoryHandler={onEditCategory}
          showNotification={showNotification}
          async
        ></EditCategoryModal>
      )}
      {deleteMenuWarningModal}
      {deleteCategoryWarningModal}
      {deleteProgramModalOpen && (
        <DeletionModal
          open={deleteProgramModalOpen}
          onCancel={() => setDeleteProgramModalOpen()}
          onDeleteClick={() => onDeleteProgram(selectedProgram)}
          entityType="Program"
          name={selectedProgram}
        />
      )}
      {deleteProgramWarningModal}
    </PageLayout>
  );
};
